import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Box, IconButton, Typography, TextField, Paper, Stack, Avatar, Skeleton, Button } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { chatPhotoUrl, uploadedFileUrl } from '../../utils/constants/links';
import ChatMessagesSkeleton from './ChatMessageSkeleton';
import GroupDetails from './GroupDetails';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
export default function ChatBox({
  messages = [],
  chat,
  onBack,
  onClose,
  loginedUserId,
  onSend,
  onTyping,
  isTyping,
  messagesLoading,
  selectChat,
  loadMoreMessages,
  hasMore,
  loadingMore,
  getChatList,
  handleBackToList,
}) {
  const [text, setText] = useState('');
  const bottomRef = useRef(null);
  const [chatDetailsOpen, setChatDetailsOpen] = useState(false);
  const containerRef = useRef(null);
  const shouldSrollToBottomRef = useRef(true);
  const prevScrollHeightRef = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    //EL SCROLL HEIGHT IS TOTAL HEIGHT OF THE CONTENT INSIDE THE CONTAINER BOTH VISIBLE AND NON VISIBLE
    //EL SCROLLTOP IS HOW MUCH THE USER HAS SCROLLED FROM TOP 0 IS VERY TOP BIGGER NUMBERS AS YOU SCROLL DOWN
    //EL CLIENTHEIGHT IS VISIBLE HEIGHT OF THE CONTAINER LIKE IF YOU CAN SEE 500PX HEIGHT OF CONTENT AT A TIME IT WILL BE 500 CONSTANT VALUE ONLY CHAGNE WITH RESOPNSIVE OR RESIZE

    const onScroll = () => {
      if (el.scrollTop == 0 && hasMore && !loadingMore) {
        prevScrollHeightRef.current = el.scrollHeight; //STORING OLD VISIBLE HEIGHT BEFORE LOADING MORE MESSAGES
        loadMoreMessages(); //IF OLD HEITT IS 300PX AND NEW MESSGE HAS NEED 500PX THEN NEW SCROLL HEIGHT WILL BE 800PX
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, loadingMore]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (prevScrollHeightRef.current) {
      const diff = el.scrollHeight - prevScrollHeightRef.current; //CALCULATING THE DIFFERENCE IN HEIGHT AFTER LOADING MORE MESSAGES
      el.scrollTop = diff; //ADJUSTING SCROLLTOP TO KEEP THE VIEW STABLE AFTER LOADING MORE MESSAGES

      prevScrollHeightRef.current = 0; //RESETTING OLD HEIGHT AFTER ADJUSTING SCROLLTOP
    }
  }, [messages]);

  const handleChange = (e) => {
    setText(e.target.value);
    if (onTyping) {
      onTyping();
    }
  };

  const send = () => {
    if (!text.trim()) return;
    onSend(text);
    setText('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    if (shouldSrollToBottomRef.current) {
      scrollToBottom();
    }
  }, [chat]);

  // Format timestamp helper
  const formatTime = (ts) => {
    return new Date(ts).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const isGroup = chat && chat?.type === 'GROUP'; // simplistic check
  const isAi = chat?.isAi;
  const name = isAi ? 'AI Assistant' : chat?.type === 'GROUP' ? chat?.group_name : chat?.participants?.[0]?.name || 'Unknown';

  const avatarImage = isAi 
    ? 'https://student.intervaledu.com/_next/image?url=https%3A%2F%2Fportal.teaminterval.net%2Fstatic%2Fmedia%2Fmap.7dd1ec7c87cddefd09e4.gif&w=128&q=75'
    : chat?.type === 'GROUP'
      ? `${uploadedFileUrl}/${chat?.group_photo}`
      : `${chatPhotoUrl}/${chat?.participants?.[0]?.photo}.jpg`;
  const onBackToChat = ({ reload = false }) => {
    if (reload) {
      selectChat(chat?.chat_room_id);
    }
    setChatDetailsOpen(false);
  };

  const getMessageDateLabel = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();

    const startOfToday = new Date(now.setHours(0, 0, 0, 0));
    const startOfMsgDay = new Date(date.setHours(0, 0, 0, 0));

    const diffDays = Math.floor((startOfToday - startOfMsgDay) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';

    // Current week → show weekday
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

    if (startOfMsgDay >= startOfWeek) {
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    }

    // Older → full date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const isNewDate = (currentMsg, previousMsg) => {
    if (!previousMsg) return true;

    const curr = new Date(currentMsg.created_at).toDateString();
    const prev = new Date(previousMsg.created_at).toDateString();

    return curr !== prev;
  };

  const DateSeparator = ({ label }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        my: 1.5,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 0.4,
          borderRadius: 10,
          bgcolor: 'rgba(0,0,0,0.08)',
          fontSize: '0.7rem',
          fontWeight: 500,
          color: 'text.secondary',
        }}
      >
        {label}
      </Box>
    </Box>
  );

  return (
    <>
      {chatDetailsOpen ? (
        <GroupDetails
          chatDetails={chat}
          onBack={onBackToChat}
          getChatList={getChatList}
          handleBackToList={handleBackToList}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}

          {/* Header */}
            <Box
            sx={{
              px: 1.8,
              py: 1.4,
              display: 'flex',
              alignItems: 'center',
              gap: 1.4,
              borderBottom: '1px solid rgba(255,255,255,0.15)',
              background: isAi 
                ? 'linear-gradient(90deg, #551364 0%, #FFD700 100%)' // Exotic Blend
                : `url("/static/illustrations/chatTop.png")`,
              bgcolor: isAi ? undefined : '#551364',
              backdropFilter: 'blur(6px)',
              color: '#ffffff',
            }}
            onClick={() => isGroup && setChatDetailsOpen(true)}
          >
            {/* Back */}
            {/* <IconButton size="small" onClick={onBack} sx={{ color: '#ffffff' }}>
              <Icon icon="mdi:arrow-left" />
            </IconButton> */}
            <IconButton
              size="small"
              onClick={onBack}
              sx={{
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.4)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              }}
            >
              <ArrowBackIcon sx={{ color: '#fff', fontSize: 15 }} />
            </IconButton>

            {/* Avatar + online dot */}
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={avatarImage}
                sx={{
                  width: 40,
                  height: 40,
                  border: '2px solid rgba(255,255,255,0.4)',
                }}
              >
                {name[0]?.toUpperCase()}
              </Avatar>

              {/* Online dot (only for 1–1 and not typing) */}
              {!isGroup && !isAi && chat?.participants?.[0]?.is_online && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    width: 10,
                    height: 10,
                    bgcolor: '#22c55e',
                    borderRadius: '50%',
                    border: '2px solid #0c3e6b',
                  }}
                />
              )}
            </Box>

            {/* Name + status */}
            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', ml: 0.5 }}>
              <Typography fontWeight={700} fontSize="0.95rem" noWrap>
                {name}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  opacity: 0.85,
                  fontSize: '0.72rem',
                  letterSpacing: '0.02em',
                }}
                noWrap
              >
                {isTyping
                  ? 'Typing…'
                  : isAi 
                    ? 'Always here to help'
                    : isGroup
                      ? `${chat?.member_count} members`
                      : chat?.participants?.[0]?.is_online
                        ? 'Online'
                        : 'Offline'}
              </Typography>
            </Box>

            {/* Close */}
            {/* <IconButton size="small" onClick={onClose} sx={{ color: '#ffffff' }}>
              <HighlightOffIcon sx={{ color: '#fff', fontSize: 20 }} />
            </IconButton> */}
          </Box>

          {/* Messages Area */}
          <Box
            ref={containerRef}
            sx={{
              flexGrow: 1,
              minHeight: 0,
              overflowY: 'auto',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              backgroundImage: `url("/static/illustrations/chatBackgroud.png")`,
              backgroundSize: '250px 250px',
              backgroundPosition: 'center',
              backgroundColor: '#f9ecff',
            }}
          >
            {loadingMore && (
              <Typography variant="caption" align="center">
                Loading older messages...
              </Typography>
            )}
            {messagesLoading ? (
              <ChatMessagesSkeleton isGroup={isGroup} />
            ) : (
              messages.map((msg, index) => {
                const isMe = msg.sender === loginedUserId;
                const showDate = isNewDate(msg, messages[index - 1]);
                return (
                  <React.Fragment key={msg.message_id}>
                    {/* DATE SEPARATOR */}
                    {showDate && <DateSeparator label={getMessageDateLabel(msg.created_at)} />}
                    <Stack
                      key={msg.message_id}
                      direction="column"
                      alignItems={isMe ? 'flex-end' : 'flex-start'}
                      maxWidth="100%"
                    >
                      {/* <Box>
                        {!isMe && isGroup && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              ml: 0.5,
                            }}
                          >
                            <Avatar
                              src={`${chatPhotoUrl}/${msg.sender_user?.photo}.jpg`}
                              sx={{
                                width: 20, // 👈 smaller avatar
                                height: 20,
                                fontSize: '0.65rem',
                                bgcolor: 'primary.light',
                              }}
                            >
                              {msg.sender_user?.name?.[0]?.toUpperCase()}
                            </Avatar>

                            <Typography
                              variant="inherit"
                              sx={{
                                fontSize: '0.55rem',
                                fontWeight: 500,
                                color: 'text.secondary',
                              }}
                            >
                              {msg.sender_user?.name}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Paper
                        elevation={0}
                        sx={{
                          my: 0.4,
                          px: 2,
                          py: 1.2,
                          maxWidth: '72%',
                          borderRadius: 2.2,

                          borderTopRightRadius: isMe ? 0 : 12,
                          borderTopLeftRadius: isMe ? 12 : 0,
                          bgcolor: isMe ? '#2f72b3' : '#8135b5f0',
                          color: isMe ? '#ffffff' : '#ffffff',
                          border: isMe ? 'none' : '1px solid rgba(86,31,110,0.12)',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.85rem',
                            lineHeight: 1.45,
                            wordBreak: 'break-word',
                          }}
                        >
                          {msg.message}
                        </Typography>
                      </Paper>

                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.2,
                          px: 0.5,
                          fontSize: '0.65rem',
                          color: 'text.disabled',
                        }}
                      >
                        {formatTime(msg.created_at)}
                      </Typography> */}

                      {msg?.message_type === 'NOTIFY' && (
                        <Typography
                          align="center"
                          sx={{
                            backgroundColor: '#f5f5f5',
                            color: '#555',
                            fontSize: '12px',
                            px: 2,
                            py: 0.5,
                            borderRadius: '16px',
                            width: 'fit-content',
                            mx: 'auto',
                            my: 1,
                          }}
                        >
                          {msg?.message}
                        </Typography>
                      )}

                      {/* TEXT / FILE / IMAGE MESSAGE */}
                      {['TEXT', 'FILE', 'IMAGE'].includes(msg?.message_type) && (
                        <>
                          {/* Sender info (group + not me) */}
                          {!isMe && isGroup && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                ml: 0.5,
                                mb: 0.2,
                              }}
                            >
                              <Avatar
                                src={
                                  msg.sender_user?.photo ? `${chatPhotoUrl}/${msg.sender_user.photo}.jpg` : undefined
                                }
                                sx={{
                                  width: 20,
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: 'primary.light',
                                }}
                              >
                                {msg.sender_user?.name?.[0]?.toUpperCase()}
                              </Avatar>

                              <Typography
                                sx={{
                                  fontSize: '0.55rem',
                                  fontWeight: 500,
                                  color: 'text.secondary',
                                }}
                              >
                                {msg.sender_user?.name}
                              </Typography>
                            </Box>
                          )}

                          {/* Message bubble */}
                          <Paper
                            elevation={isAi ? 4 : 0}
                            sx={{
                              my: 0.4,
                              px: 2,
                              py: 1.2,
                              maxWidth: '72%',
                              borderRadius: 2.2,
                              borderTopRightRadius: isMe ? 0 : 12,
                              borderTopLeftRadius: isMe ? 12 : 0,
                              bgcolor: isMe ? '#2f72b3' : (msg.sender === 'AI' ? '#551364' : '#8135b5f0'),
                              color: '#ffffff',
                              border: msg.sender === 'AI' ? '1px solid #FFD700' : (isMe ? 'none' : '1px solid rgba(86,31,110,0.12)'),
                              boxShadow: msg.sender === 'AI' ? '0 0 10px rgba(255, 215, 0, 0.3)' : 'none',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.85rem',
                                lineHeight: 1.45,
                                wordBreak: 'break-word',
                              }}
                            >
                              {msg.message}
                            </Typography>
                          </Paper>

                          {/* Time */}
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.2,
                              px: 0.5,
                              fontSize: '0.65rem',
                              color: 'text.disabled',
                            }}
                          >
                            {formatTime(msg.created_at)}
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </React.Fragment>
                );
              })
            )}

            <div ref={bottomRef} />
          </Box>

          {/* Message end area */}
          <Box sx={{ p: 1.5, bgcolor: '#f9ecff' }}>
            <Box
              sx={{
                p: 1,
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: '#ffffff',
                borderRadius: 3,
              }}
            >
              {/* <IconButton
                sx={{
                  color: '#2f72b3',
                  bgcolor: '#ffffff',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                  borderRadius: '50%',
                  width: 42,
                  height: 42,
                  '&:hover': {
                    bgcolor: '#f3e8ff',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.22)',
                  },
                }}
              >
                <Icon icon="mdi:paperclip" fontSize={20} />
              </IconButton> */}

              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Type a message..."
                value={text}
                onChange={handleChange}
                onKeyDown={handleKeyPress}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />

              <IconButton
                onClick={send}
                disabled={!text.trim() || messagesLoading}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                }}
              >
                <Icon icon="mdi:send" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
